import {put} from 'redux-saga/effects';
import {isEmpty, omit, createSelector, objectDifference, isEqual, createStructuredSelector} from 'helpers';
import {watch, runWhile} from 'store/effects';
import {user} from 'store/session';
import {hasUnprocessedChanges} from 'store/server';
import {settings as getSettings} from 'store/settings';

export function* changes() {
  yield watch(user, function* (user) {
    yield runWhile(computeChanges, Boolean(user));
  });
  yield watch(hasUnprocessedChanges, function* (value) {
    window.onbeforeunload = () => value ? 'Reload site? Changes that you made may not be saved.' : undefined;
  });
}

function* computeChanges() {
  for (const { selector, worker } of [ data, clarity, identity, settings ]) {
    yield watch(selector, worker);
  }
}

const data = {
  selector: createStructuredSelector({
    activities: state => state.activities.index,
    collaborators: state => state.collaborators,
    emotionsMeter: state => state.diary.emotionsMeter,
    files: state => state.files,
    goalProgress: state => state.goals.progress,
    goals: state => state.goals.index,
    goalTypes: state => state.goals.types,
    journals: state => state.diary.index,
    notes: state => state.notes.index,
    noteTypes: state => state.notes.types,
    questions: state => state.questions,
    relations: state => state.relations,
    sortingInfo: state => state.sortingInfo,
    statuses: state => state.activities.progress,
    subTasks: state => state.activities.subs,
    tags: state => state.tags,
    units: state => state.units,
    lists: state => state.lists.index,
    listItems: state => state.lists.listItems,
  }),

  worker: function* (current, previous, { type }) {
    if (/SERVER_DATA_RECEIVED|SESSION_LOGOUT/.test(type) || !previous) {
      return;
    }
    for (const modelType in current) {
      const [ now, prev ] = [ current[modelType], previous[modelType] ];
      if (now !== prev) {
        for (const id in now) {
          if (!prev[id]) {
            yield put({
              type: 'APP_DATA_CHANGED',
              action: 'create',
              id,
              initiator: type,
              model: now[id],
              modelType,
            });
          } else if (prev[id] && prev[id] != now[id] && !isEqual(now[id], prev[id])) {
            yield put({
              type: 'APP_DATA_CHANGED',
              action: 'update',
              id,
              initiator: type,
              modelType,
              now: now[id],
              prev: prev[id],
            });
          }
        }
        for (const id in prev) {
          if (!now[id]) {
            yield put({
              type: 'APP_DATA_CHANGED',
              action: 'delete',
              id,
              initiator: type,
              model: prev[id],
              modelType,
            });
          }
        }
      }
    }
  }
}

const settings = {
  selector: createSelector(getSettings, settings => omit(settings, 'today.date', 'gptConnects')), //ignore today.date
  worker: function* (current, previous, { type }) {
    if (!/SERVER_DATA_RECEIVED|SESSION_LOGOUT/.test(type) && previous && !isEmpty(objectDifference(current, previous))) {
      yield put({
        type: 'APP_SETTINGS_CHANGED',
        action: 'settings',
        payload: objectDifference(current, previous),
      });
    }
  }
}

const clarity = {
  selector: state => state.clarity,
  worker: function* (current, previous, { type, timestamp_ms }) {
    if (!/SERVER_CLARITY_LOADED|SESSION_LOGOUT/.test(type) && previous) {
      yield put({
        type: 'APP_CLARITY_CHANGED',
        change: {
          action: 'clarity',
          attempts: 0,
          payload: current,
          timestamp: timestamp_ms,
        }
      });
    }
  }
}

const identity = {
  selector: state => state.identity,
  worker: function* (current, previous, { type, timestamp_ms }) {
    if (!/SERVER_IDENTITY_LOADED|SESSION_LOGOUT/.test(type) && previous) {
      yield put({
        type: 'APP_IDENTITY_CHANGED',
        change: {
          action: 'identity',
          attempts: 0,
          payload: current,
          timestamp: timestamp_ms,
        }
      });
    }
  }
}
